import React, { FC, useState } from 'react';
import { REPORT_BASE_PATH, REPORT_NEWS_PATH, REPORT_NEWS_COMMENTS } from '../../../../constants';
import { PaginateList } from '../../../../../../common/types/common-types';
import { ReportItem } from '../../../../types/report-types';
import { useDataProvider, getDayStart, getDayEnd, getDefaultDisplayDate } from 'src/app/common/utils';
import { ReportListParams, fetchNewsCommentsReportList, fetchReportDownloadLink } from '../../../../network/reportCrud';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import { ParamsProps } from '../../../../../../common/components/ParamsProvider';
import { useHistory } from 'react-router-dom';
import PruTable from '../../../../../../common/components/PruTable/PruTable';
import PruFilter, { PruFilterItemType } from '../../../../../../common/components/PruTable/PruFilter';

type CommentsListingPageProps = {} & ParamsProps;

const initialState: ReportListParams = {
  requestedDateFrom: null,
  requestedDateTo: null,
  page: 1,
  limit: 5,
};

const paramsInitiator = (initialParams?: Record<string, string>): ReportListParams => {
  return initialParams
    ? {
        requestedDateFrom: initialParams.requestedDateFrom
          ? getDayStart(new Date(initialParams.requestedDateFrom))
          : null,
        requestedDateTo: initialParams.requestedDateTo ? getDayEnd(new Date(initialParams.requestedDateTo)) : null,
        page: 1,
        limit: 5,
      }
    : initialState;
};

const CommentsListingPage: FC<CommentsListingPageProps> = ({ initialParams, onChangeQueryParams }) => {
  const history = useHistory();
  const intl = useIntl();
  const Translation = (id: string) => intl.formatMessage({ id });
  const dispatch = useDispatch();

  const [filterState, setFilterState] = useState<ReportListParams>(paramsInitiator(initialParams));

  const [reportList, setReportList] = useState<PaginateList<ReportItem>>();

  const { isLoading, refreshData } = useDataProvider<PaginateList<ReportItem>>(
    async () => {
      try {
        if (onChangeQueryParams) {
          onChangeQueryParams(filterState);
        }
        return await fetchNewsCommentsReportList(filterState, dispatch);
      } catch (err) {}
    },
    setReportList,
    false,
  );

  const fetchCsvFile = async (reportId: string) => {
    try {
      const reportItem = await fetchReportDownloadLink(reportId, dispatch);
      if (reportItem) {
        window.open(reportItem.url);
      }
    } catch (err) {}
  };

  return (
    <>
      <PruFilter
        title={Translation('report.news.comments.filter')}
        itemDef={[
          {
            type: PruFilterItemType.DATE_RANGE,
            fieldFrom: 'requestedDateFrom',
            fieldTo: 'requestedDateTo',
            initialDateFrom: filterState.requestedDateFrom,
            initialDateTo: filterState.requestedDateTo,
            displayName: Translation('report.common.requestDate'),
          },
        ]}
        onChangeFilter={(data) =>
          setFilterState({
            ...filterState,
            requestedDateFrom: data.requestedDateFrom,
            requestedDateTo: data.requestedDateTo,
          })
        }
        fetchData={refreshData}
      />
      <PruTable
        disableBulkSelect
        dataSource={reportList?.items}
        isLoading={isLoading}
        onRefresh={refreshData}
        title={Translation('report.news.comments.list')}
        headerBtnDef={[
          {
            color: 'primary',
            title: Translation('report.requestReport'),
            onClick: () => history.push(`${REPORT_BASE_PATH}${REPORT_NEWS_PATH}${REPORT_NEWS_COMMENTS}/create`),
          },
        ]}
        operationDef={[
          {
            title: Translation('section.common.operation.download'),
            tooltipText: 'Download Report',
            onClick: (row) => fetchCsvFile(row._id),
            condition: (row) => row.status === 'DONE',
          },
        ]}
        columnDef={[
          {
            isId: true,
            hidden: true,
            keyIndex: '_id',
            displayName: '',
            renderData: () => '',
          },
          {
            keyIndex: 'requestedDate',
            displayName: Translation('report.common.requestDate'),
            renderData: (row) => getDefaultDisplayDate(new Date(row.requestedDate), 'datetime'),
          },
          {
            keyIndex: 'parameters',
            align: 'center',
            displayName: Translation('report.news.common.id'),
            renderData: (row) => row.parameters.newsId || '-',
          },
          {
            keyIndex: 'status',
            align: 'center',
            displayName: Translation('report.common.status'),
            renderData: (row) => row.status,
          },
        ]}
        totalPages={reportList?.totalPages}
        totalRecords={reportList?.total}
        onChangePage={(page, rowsPerPage) => {
          setFilterState({
            ...filterState,
            page,
            limit: rowsPerPage,
          });
          refreshData();
        }}
      />
    </>
  );
};

export default CommentsListingPage;
